import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { replacePlaceholder } from '@/helpers/cms';
import { TopSpotGameStatus } from '@/helpers/topSpotGame';
import { useToggle } from '@/hooks/common';
import { useNavigation } from '@/hooks/navigation';
import { useUserVipLevel } from '@/hooks/useUserVipLevel';
import { useCMSTopSpotGame } from '@/modules/vip';
import { ProgressBar } from '@/modules/vip/TopSpotGame/components/ProgressBar';
import { TopSpotTimer } from '@/modules/vip/TopSpotGame/components/TopSpotInfo/components/TopSpotTimer';
import { useCommonStore } from '@/store';

import { ScCashupBonanzaSection } from './styled';

export interface TopSpotGameSection {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const TopSpotGameSection: FC<TopSpotGameSection> = ({
  isMobile,
  onLinkClick,
}) => {
  const { t } = useTranslation(['common']);

  const [isFullInfoOpen, toggleFullInfo] = useToggle();
  const { topSpotCMSData } = useCMSTopSpotGame();
  const {
    userTSGProgressPercentByCP,
    userTarget,
    user,
    topSpotGameStatus,
    topSpotGameStaticData,
    isPassedSuperSpotBreakpoint,
  } = useCommonStore(
    useShallow((state) => ({
      userTSGProgressPercentByCP: state.userTSGProgressPercentByCP,
      userTarget: state.userTarget,
      user: state.user,
      topSpotGameStatus: state.topSpotGameStatus,
      topSpotGameStaticData: state.topSpotGameStaticData,
      isPassedSuperSpotBreakpoint: state.isPassedSuperSpotBreakpoint,
    }))
  );

  const { navigate } = useNavigation();
  const {
    images: {
      arrowDownGrayIcon: { component: ArrowDownGrayIcon },
    },
  } = useTheme();

  const isPVIP = topSpotGameStatus === TopSpotGameStatus.PVIP;
  const isActive = topSpotGameStatus === TopSpotGameStatus.ACTIVE;
  const isComplete = topSpotGameStatus === TopSpotGameStatus.COMPLETE;

  const userVipLevel = useUserVipLevel(user);
  if (!topSpotCMSData || !topSpotGameStaticData) return null;

  const completeText = useMemo(
    () =>
      replacePlaceholder(
        topSpotCMSData.gameCompleteText,
        'vip',
        t(topSpotGameStaticData.vipTypeName)
      ),
    [topSpotCMSData, topSpotGameStaticData, t]
  );

  const pvipContent = (
    <span>
      <b>{topSpotCMSData?.pvipText}</b>
    </span>
  );

  const completeContent = (
    <span>
      <b>{completeText}</b>
    </span>
  );

  const vipContent = (
    <>
      <span>
        <b>{t('timeLeft')}:</b>
        <TopSpotTimer />
      </span>
      <span>
        <b>{t(topSpotGameStaticData.prizeText)}:</b>
        {userTarget} CP
      </span>
    </>
  );

  const superVipContent = (
    <>
      <span>
        <strong>
          {t(`vip:topSpotInfo.currentSpot.${userVipLevel?.toLowerCase()}`)}:
        </strong>
        {t('vip:topSpotInfo.currentSpot.secured')}
      </span>
      <span>
        <strong>{t(topSpotGameStaticData.prizeText)}:</strong>
        {userTarget} CP
      </span>
    </>
  );

  const activeContent = isPassedSuperSpotBreakpoint
    ? superVipContent
    : vipContent;

  return (
    <ScCashupBonanzaSection
      showFullInfo={isFullInfoOpen}
      onClick={() => isMobile && toggleFullInfo()}
    >
      <div className="info">
        <div className="level-info">
          <div className="text">
            {isPVIP && pvipContent}
            {isActive && activeContent}
            {isComplete && completeContent}
          </div>

          <div className="addons">
            {isMobile && (
              <>
                <ImageComponent
                  src={topSpotGameStaticData.dashboardImg}
                  width={65}
                  height={40}
                  alt="logo"
                  objectFit="contain"
                />
                <ArrowDownGrayIcon />
              </>
            )}
          </div>
        </div>

        {isActive &&
          (!isMobile || isFullInfoOpen) &&
          (isPassedSuperSpotBreakpoint ? (
            <span>
              <b>{t('timeLeft')}:</b>
              <TopSpotTimer />
            </span>
          ) : (
            <span>
              <b>{t('topSpotInfo.yourProgress')}:</b>
              {userTSGProgressPercentByCP}%
            </span>
          ))}
      </div>

      {isActive && <ProgressBar small userVipLevel={userVipLevel} />}

      {(!isMobile || isFullInfoOpen) && (
        <Button
          grayTransparentBg
          wide
          small
          onClick={() => {
            onLinkClick && onLinkClick();
            navigate('/vip', true);
          }}
        >
          {t('common:details')}
        </Button>
      )}
    </ScCashupBonanzaSection>
  );
};
